import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Card } from '@/src/elements/card'
import { Textarea } from '@/src/elements/input'
import { Modal } from '@/src/elements/modal/Modal'
import { Typography } from '@/src/elements/text/Typography'
import type { TThanksMessageType, TUplift } from '@/src/features/intention/api/types/getIntentionPrayersApi.types'
import { useReplyToUpliftMutation } from '@/src/features/message/services/useReplyToUpliftMutation'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { useTranslate } from '@/src/lang/useTranslate'

type TAnswerUpliftModalProps = {
  uplift?: (TUplift & { prayerId: number }) | null
  showModal: boolean
  onClose: VoidFunction
  onConfirm: VoidFunction
  intentionId: number
  type: TThanksMessageType
}

export const AnswerUpliftModal = ({
  onClose,
  onConfirm,
  uplift,
  showModal,
  intentionId,
  type,
}: TAnswerUpliftModalProps): ReactNode => {
  const [answerMessage, setAnswerMessage] = useState<string>('')
  const { t } = useTranslate()
  const { addToast } = useToast()

  const { mutateAsync } = useReplyToUpliftMutation({ intentionId, type, prayerId: uplift?.prayerId })

  const handleSubmit = () => {
    if (!uplift) return
    mutateAsync({
      payload: { text: answerMessage },
      uplift: { id: uplift.id },
    })
      .then(onConfirm)
      .catch((error) => {
        addToast({
          message: t(parseApiError(error) || 'uplift.answer.error'),
          colors: 'danger',
        })
      })
  }

  return (
    <Modal
      title={<FormattedMessage id="intention.answer.uplift" />}
      onCancel={onClose}
      onConfirm={handleSubmit}
      showModal={showModal}
      showCancelButton
      confirmText={t('word.send')}
      confirmButtonProps={{ isDisabled: answerMessage.length === 0 }}
    >
      <View className="flex w-full flex-col gap-6 py-2">
        <Card className="rounded-xl bg-default-bg-light p-4">
          <Typography>{uplift?.message}</Typography>
        </Card>
        <View className="flex w-full flex-col gap-1">
          <Typography bold>
            <FormattedMessage id="words.your-message" />
          </Typography>
          <Textarea
            inBottomSheet
            testID={TEST_ID.INTENTION.UPLIFT.ANSWER.TEXTAREA}
            color="default"
            value={answerMessage}
            onChangeText={setAnswerMessage}
            numberOfLines={3}
          />
        </View>
      </View>
    </Modal>
  )
}
